import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Dimensions, Platform } from 'react-native';
import { Text, TextInput, Button, useTheme, Surface, IconButton } from 'react-native-paper';
import { router, useLocalSearchParams, Stack } from 'expo-router';
import { confirmPasswordReset } from '../../src/api/apiService';
import { useTranslation } from 'react-i18next';
import * as Animatable from 'react-native-animatable';
import { StatusBar } from 'expo-status-bar';
import Logo from '../../src/components/Logo';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { z } from 'zod';

const { width } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// API response types
type PasswordResetErrorResponse = {
  detail?: string;
  new_password1?: string[];
  new_password2?: string[];
  token?: string[];
  uid?: string[];
  non_field_errors?: string[];
};

// Password validation schema
const passwordSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
});

export default function ResetPasswordConfirm() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { uid, token } = useLocalSearchParams();
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [loading, setLoading] = useState(false);
  const [secure1, setSecure1] = useState(true);
  const [secure2, setSecure2] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!uid || !token) {
      setMessage({
        type: 'error',
        text: t('invalidResetLink', 'Link de redefinição de senha inválido. Por favor, solicite um novo.')
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, token]);

  const validatePassword = (password: string): string[] => {
    try {
      passwordSchema.parse({ password });
      return [];
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.issues.map(err => err.message);
      }
      return [t('passwordValidationError', 'Password validation error')];
    }
  };

  const handlePasswordChange = (password: string) => {
    setNewPassword1(password);
    setValidationErrors(validatePassword(password));
  };

  const handleResetPasswordConfirm = async () => {
    // Reset previous messages
    setMessage(null);
    
    // Validate passwords
    if (!newPassword1 || !newPassword2) {
      setMessage({
        type: 'error',
        text: t('allFieldsRequired', 'Por favor, preencha todos os campos.')
      });
      return;
    }

    if (newPassword1 !== newPassword2) {
      setMessage({
        type: 'error',
        text: t('passwordsDontMatch', 'As senhas não coincidem.')
      });
      return;
    }

    const errors = validatePassword(newPassword1);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const response = await confirmPasswordReset(
        uid as string,
        token as string,
        newPassword1,
        newPassword2
      );

      if (response.ok) {
        setMessage({
          type: 'success',
          text: t('passwordResetSuccess', 'Sua senha foi redefinida com sucesso.')
        });
        // Clear the form
        setNewPassword1('');
        setNewPassword2('');
        // Redirect after a short delay
        setTimeout(() => {
          router.replace('/?reset=success');
        }, 2000);
      } else {
        console.error('Password reset error:', response.data);
        let errorMessage = t('passwordResetError', 'Falha ao redefinir a senha.');
        
        const errorData = response.data as PasswordResetErrorResponse;
        if (errorData) {
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.new_password1) {
            errorMessage = errorData.new_password1.join(', ');
          } else if (errorData.new_password2) {
            errorMessage = errorData.new_password2.join(', ');
          } else if (errorData.token) {
            errorMessage = errorData.token.join(', ');
          } else if (errorData.uid) {
            errorMessage = errorData.uid.join(', ');
          } else if (errorData.non_field_errors) {
            errorMessage = errorData.non_field_errors.join(', ');
          }
        }
        
        setMessage({
          type: 'error',
          text: errorMessage
        });
      }
    } catch (error) {
      console.error('Password reset exception:', error);
      setMessage({
        type: 'error',
        text: t('networkError', 'Ocorreu um erro de rede. Por favor, tente novamente.')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Surface style={[styles.pageContainer, { backgroundColor: theme.colors.background }]}>
      <Stack.Screen options={{ headerShown: false }} />
      <StatusBar style="light" />
      <ScrollView contentContainerStyle={styles.scrollContainer} keyboardShouldPersistTaps="handled">
        <Surface style={[styles.container, { backgroundColor: theme.colors.surface }]}>
          <Animatable.View animation="fadeIn" duration={600} style={styles.logoContainer}>
            <Logo size="small" style={styles.logo} />
          </Animatable.View>
          
          <Animatable.View animation="fadeInUp" duration={800} delay={300}>
            <Text style={[styles.title, { color: theme.colors.primary }]}>
              {t('resetPasswordTitle')}
            </Text>

            {message && (
              <Animatable.View 
                animation="fadeIn" 
                duration={800} 
                style={styles.messageContainer}
              >
                <View style={[
                  styles.message,
                  { backgroundColor: message.type === 'success' ? theme.colors.primary : theme.colors.error }
                ]}>
                  <MaterialCommunityIcons 
                    name={message.type === 'success' ? "check-circle" : "alert-circle"} 
                    size={24} 
                    color="white" 
                  />
                  <Text style={styles.messageText}>{message.text}</Text>
                </View>
              </Animatable.View>
            )}
            
            <Text style={[styles.description, { color: theme.colors.onSurfaceVariant }]}>
              {t('enterNewPassword')}
            </Text>
            
            <View style={styles.formContainer}>
              <View style={styles.inputContainer}>
                <TextInput
                  label={t('newPasswordPlaceholder')}
                  value={newPassword1}
                  onChangeText={handlePasswordChange}
                  secureTextEntry={secure1}
                  style={styles.input}
                  mode="outlined"
                  activeOutlineColor={theme.colors.primary}
                  right={
                    <TextInput.Icon 
                      icon={secure1 ? "eye-off" : "eye"} 
                      onPress={() => setSecure1(!secure1)}
                      color={theme.colors.primary}
                    />
                  }
                />
                {validationErrors.map((error, index) => (
                  <Text key={index} style={[styles.errorText, { color: theme.colors.error }]}>
                    {error}
                  </Text>
                ))}
              </View>

              <View style={styles.inputContainer}>
                <TextInput
                  label={t('confirmPasswordPlaceholder')}
                  value={newPassword2}
                  onChangeText={setNewPassword2}
                  secureTextEntry={secure2}
                  style={styles.input}
                  mode="outlined"
                  activeOutlineColor={theme.colors.primary}
                  right={
                    <TextInput.Icon 
                      icon={secure2 ? "eye-off" : "eye"} 
                      onPress={() => setSecure2(!secure2)}
                      color={theme.colors.primary}
                    />
                  }
                />
              </View>

              <Button
                mode="contained"
                onPress={handleResetPasswordConfirm}
                loading={loading}
                disabled={loading || !uid || !token || !newPassword1 || !newPassword2 || validationErrors.length > 0}
                style={[styles.button, { backgroundColor: theme.colors.primary }]}
                labelStyle={{ color: theme.colors.onPrimary, fontSize: 16, fontWeight: 'bold' }}
                contentStyle={styles.buttonContent}
              >
                {t('changePasswordButton')}
              </Button>

              <Button
                mode="text"
                onPress={() => router.replace('/')}
                style={styles.backButton}
                textColor={theme.colors.primary}
                icon="arrow-left"
              >
                {t('back', 'Voltar')}
              </Button>
            </View>
          </Animatable.View>
        </Surface>
      </ScrollView>
    </Surface>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 16,
    minHeight: '100%',
  },
  container: {
    padding: 24,
    justifyContent: 'center',
    borderRadius: 16,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    maxWidth: isWeb ? 480 : '100%',
    width: '100%',
    alignSelf: 'center',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logo: {
    width: 120,
    height: 120,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  description: {
    textAlign: 'center',
    marginBottom: 24,
    fontSize: 16,
    lineHeight: 22,
  },
  formContainer: {
    width: '100%',
  },
  inputContainer: {
    marginBottom: 16,
  },
  input: {
    backgroundColor: 'transparent',
  },
  button: {
    marginTop: 24,
    borderRadius: 8,
  },
  buttonContent: {
    paddingVertical: 8,
    height: 50,
  },
  backButton: {
    marginTop: 16,
  },
  messageContainer: {
    marginBottom: 20,
  },
  message: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
  },
  messageText: {
    color: 'white',
    marginLeft: 8,
    flex: 1,
  },
  errorText: {
    fontSize: 12,
    marginTop: 4,
  },
}); 